import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Fila "próxima ação".
 *
 * O CRM guardava tudo e não cobrava nada: negócio parado há três semanas,
 * proposta sem resposta, cobrança vencida — a informação existia, mas dependia
 * de alguém ir procurar.
 *
 * A lista é curta de propósito. Trinta itens são ignorados tão rápido quanto
 * lista nenhuma; o valor está em mostrar as poucas coisas que custam caro
 * hoje, ordenadas por consequência.
 */
const useStyles = makeStyles((theme) => ({
  paper: { padding: theme.spacing(2), height: "100%" },
  cabecalho: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    "&:last-child": { borderBottom: 0 },
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  faixa: { width: 4, alignSelf: "stretch", borderRadius: 2, flex: "none" },
  corpo: { flex: 1, minWidth: 0 },
  titulo: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sugestao: { color: theme.palette.text.secondary },
  vazio: { textAlign: "center", padding: theme.spacing(4, 2) },
  ok: { fontSize: 40, color: theme.palette.success.main },
}));

/** Cor por tipo — a mesma escala do painel, para não inventar significado. */
const COR = {
  cobranca_vencida: "#DC2626",
  proposta_sem_resposta: "#EA580C",
  deal_fechamento_vencido: "#A16207",
  tarefa_atrasada: "#9333EA",
  deal_parado: "#2563EB",
  deal_sem_atividade: "#0891B2",
  cliente_sem_contato: "#65A30D",
};

const ROTULO = {
  cobranca_vencida: "Cobrança",
  proposta_sem_resposta: "Proposta",
  deal_fechamento_vencido: "Fechamento",
  tarefa_atrasada: "Tarefa",
  deal_parado: "Negócio parado",
  deal_sem_atividade: "Sem atividade",
  cliente_sem_contato: "Cliente frio",
};

const DESTINO = {
  deal: "/deals",
  proposal: "/proposals",
  task: "/projects",
  receivable: "/finance",
  client: "/clients",
};

const moeda = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : null;

const NextActions = () => {
  const classes = useStyles();
  const history = useHistory();

  const [acoes, setAcoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [apenasMinhas, setApenasMinhas] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/next-actions", {
        params: { mine: apenasMinhas, limit: 12 },
      });
      setAcoes(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, [apenasMinhas]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = (acao) => {
    // Leva para a lista da entidade. Sem rota por id para todas elas, mandar
    // para a tela certa já poupa a busca — e é honesto sobre o que existe.
    const destino = DESTINO[acao.entity];
    if (destino) history.push(destino);
  };

  return (
    <Paper className={classes.paper} elevation={0} variant="outlined">
      <div className={classes.cabecalho}>
        <div>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Próximas ações
          </Typography>
          <Typography variant="caption" color="textSecondary">
            O que está esfriando e custa caro deixar para depois
          </Typography>
        </div>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={apenasMinhas}
              onChange={(e) => setApenasMinhas(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography variant="caption">Só minhas</Typography>}
        />
      </div>

      <Box mt={1}>
        {carregando ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : acoes.length === 0 ? (
          <div className={classes.vazio}>
            <CheckCircleOutlineIcon className={classes.ok} />
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Nada pendente
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Nenhum negócio parado, proposta sem resposta ou cobrança vencida.
            </Typography>
          </div>
        ) : (
          acoes.map((acao) => (
            <div
              key={`${acao.entity}-${acao.entityId}-${acao.kind}`}
              className={classes.item}
              onClick={() => abrir(acao)}
            >
              <div
                className={classes.faixa}
                style={{ backgroundColor: COR[acao.kind] || "#64748B" }}
              />
              <div className={classes.corpo}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Chip
                    size="small"
                    label={ROTULO[acao.kind] || acao.kind}
                    style={{
                      backgroundColor: `${COR[acao.kind] || "#64748B"}1A`,
                      color: COR[acao.kind] || "#64748B",
                      fontWeight: 600,
                      height: 20,
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {acao.reason}
                  </Typography>
                </Box>
                <Typography variant="body2" className={classes.titulo}>
                  {acao.title}
                </Typography>
                <Typography variant="caption" className={classes.sugestao}>
                  {acao.clientName ? `${acao.clientName} · ` : ""}
                  {acao.suggestion}
                </Typography>
              </div>
              {acao.value ? (
                <Typography variant="caption" style={{ fontWeight: 700 }}>
                  {moeda(acao.value)}
                </Typography>
              ) : null}
            </div>
          ))
        )}
      </Box>

      {!carregando && acoes.length > 0 && (
        <Box mt={1} textAlign="right">
          <Button size="small" onClick={carregar}>
            Atualizar
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default NextActions;
